import { PlatformIcon as PlatformIconUtil } from '../../utils/platformIcons';

// Font family utility function
export const getFontFamily = (fontId) => {
    const fontMap = {
        'default': 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        'arial': 'Arial, sans-serif',
        'helvetica': 'Helvetica, Arial, sans-serif',
        'inter': 'Inter, system-ui, sans-serif',
        'roboto': 'Roboto, system-ui, sans-serif',
        'opensans': '"Open Sans", system-ui, sans-serif',
        'lato': 'Lato, system-ui, sans-serif',
        'montserrat': 'Montserrat, system-ui, sans-serif',
        'poppins': 'Poppins, system-ui, sans-serif',
        'nunito': 'Nunito, system-ui, sans-serif',
        'sourcesans': '"Source Sans Pro", system-ui, sans-serif',
        'ubuntu': 'Ubuntu, system-ui, sans-serif',
        'raleway': 'Raleway, system-ui, sans-serif',
        'dmsans': '"DM Sans", system-ui, sans-serif',
        'worksans': '"Work Sans", system-ui, sans-serif',
        'karla': 'Karla, system-ui, sans-serif',
        'oswald': 'Oswald, system-ui, sans-serif',
        'opensans-condensed': '"Open Sans Condensed", system-ui, sans-serif',
        'questrial': 'Questrial, system-ui, sans-serif',
        'playfair': '"Playfair Display", Georgia, serif',
        'merriweather': 'Merriweather, Georgia, serif',
        'lora': 'Lora, Georgia, serif',
        'crimson': '"Crimson Text", Georgia, serif',
        'fira': '"Fira Code", "Courier New", monospace'
    };
    return fontMap[fontId] || fontMap.default;
};

// Sample review data for previews
export const sampleReviews = [
    {
        id: 1,
        name: 'Sarah Johnson',
        rating: 5,
        comment: 'Absolutely amazing service! The team went above and beyond to meet our needs. I\'ve been a customer for over two years now and they consistently deliver exceptional results. Their attention to detail and customer care is unmatched in the industry.',
        platform: 'Google',
        date: '2 days ago',
        avatar: 'SJ',
        isVerified: true
    },
    {
        id: 2,
        name: 'Michael Chen',
        rating: 5,
        comment: 'Professional, reliable, and fast delivery. Highly recommend! The entire process was seamless from start to finish.',
        platform: 'Facebook',
        date: '1 week ago',
        avatar: 'MC',
        isVerified: true
    },
    {
        id: 3,
        name: 'Emily Rodriguez',
        rating: 4,
        comment: 'Great experience overall. The product quality exceeded my expectations and the customer service was very responsive when I had questions.',
        platform: 'Amazon',
        date: '2 weeks ago',
        avatar: 'ER',
        isVerified: false
    },
    {
        id: 4,
        name: 'David Thompson',
        rating: 5,
        comment: 'Outstanding customer support and quick resolution of issues. They really care about their customers and it shows.',
        platform: 'Google',
        date: '3 weeks ago',
        avatar: 'DT',
        isVerified: true
    },
    {
        id: 5,
        name: 'Lisa Wang',
        rating: 5,
        comment: 'Best purchase I\'ve made this year. Will definitely buy again! The value for money is incredible.',
        platform: 'Amazon',
        date: '1 month ago',
        avatar: 'LW',
        isVerified: true
    },
    {
        id: 6,
        name: 'Robert Martinez',
        rating: 4,
        comment: 'Very satisfied with the service. Quick turnaround and professional results. Only minor issue was with initial communication but they resolved it quickly.',
        platform: 'Google',
        date: '1 month ago',
        avatar: 'RM',
        isVerified: false
    },
    {
        id: 7,
        name: 'Jennifer Kim',
        rating: 5,
        comment: 'Exceeded all my expectations! The quality is top-notch and the team is incredibly knowledgeable and helpful.',
        platform: 'Facebook',
        date: '5 weeks ago',
        avatar: 'JK',
        isVerified: true
    },
    {
        id: 8,
        name: 'James Wilson',
        rating: 5,
        comment: 'Fantastic experience from start to finish. They delivered exactly what was promised and more.',
        platform: 'Amazon',
        date: '6 weeks ago',
        avatar: 'JW',
        isVerified: true
    }
];

// Mock data for demo purposes to simulate 41,300 reviews
export const mockTotalReviews = 41300;
export const mockAverageRating = 4.8;

// Star rating component
export const StarRating = ({ rating, size = 'w-4 h-4', color = '#F1C40F' }) => {
    return (
        <div className="flex space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
                <svg
                    key={star}
                    className={`${size} fill-current`}
                    style={{ color: star <= rating ? color : '#d1d5db' }}
                    viewBox="0 0 24 24"
                >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
            ))}
        </div>
    );
};

// Platform icon component using dynamic platform icons from review data
export const PlatformIcon = ({ platform, iconUrl, className = "w-5 h-5", showFallback = true }) => {
    // If iconUrl is provided (from review/location data), use it directly
    if (iconUrl && typeof iconUrl === 'string' && iconUrl.trim()) {
        return (
            <div className="relative inline-block">
                <img
                    src={iconUrl}
                    alt={`${platform} icon`}
                    className={className}
                    onError={(e) => {
                        // Simple fallback - hide the image and show hardcoded icon
                        console.warn(`Failed to load platform icon: ${iconUrl}. Using fallback.`);
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = 'block';
                        }
                    }}
                />
                {showFallback && (
                    <div style={{ display: 'none' }} className="inline-block">
                        <PlatformIconUtil
                            platform={platform}
                            className={className}
                            alt={`${platform} icon`}
                        />
                    </div>
                )}
            </div>
        );
    }

    // Fallback to hardcoded platform icons
    return (
        <PlatformIconUtil
            platform={platform}
            className={className}
            alt={`${platform} icon`}
        />
    );
};

// Common verification icon component for consistency across all layouts
export const VerificationIcon = ({ size = '16', className = '', isDark = false, color = null, accentColor = null }) => {
    // Use provided color, or accent color, or default based on theme
    const iconColor = color || accentColor || (isDark ? '#60a5fa' : '#4A90E2');

    return (
        <svg
            className={`flex-shrink-0 inline-flex items-center ml-1 ${className}`}
            width={size}
            height={size}
            viewBox="0 0 20 20"
            fill={iconColor}
        >
            <path
                fillRule="evenodd"
                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
            />
        </svg>
    );
};

// Helper function to normalize review data structure
export const normalizeReviewData = (review) => {
    // Handle both database review structure and mock review structure
    if (review.reviewerName && review.platform?.name) {
        // Database structure
        return {
            id: review._id || review.id,
            name: review.reviewerName,
            rating: review.rating,
            comment: review.reviewText || '',
            platform: review.platform.name,
            date: formatDate(review.reviewDate),
            avatar: review.reviewerAvatar || generateInitials(review.reviewerName),
            isVerified: review.isVerified || false
        };
    } else {
        // Mock/legacy structure
        return {
            id: review.id,
            name: review.name || review.reviewerName,
            rating: review.rating,
            comment: review.comment || review.reviewText || '',
            platform: review.platform?.name || (typeof review.platform === 'string' ? review.platform : 'unknown'),
            date: review.date || formatDate(review.reviewDate),
            avatar: review.avatar || generateInitials(review.name || review.reviewerName),
            isVerified: review.isVerified || false
        };
    }
};

// Helper function to check if a string is a valid image URL
const isValidImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    // Check if it's a data URI image
    if (url.startsWith('data:image/')) return true;

    // Check if it's a proper HTTP/HTTPS URL
    const isUrl = url.startsWith('http://') || url.startsWith('https://');
    if (!isUrl) return false;

    // Check for common image extensions
    const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)(\?.*)?$/i;
    if (imageExtensions.test(url)) return true;

    // Check for known image hosting services (more comprehensive list)
    const imageHosts = new RegExp('(amazonaws\\.com|cloudfront\\.net|googleapis\\.com|googleusercontent\\.com|gravatar\\.com|unsplash\\.com|imgur\\.com|cloudinary\\.com|imagekit\\.io|uploadcare\\.com|cloudflare\\.com/[^/]*/image|facebook\\.com/[^/]*/picture|graph\\.facebook\\.com|fbcdn\\.net|twimg\\.com|linkedin\\.com/[^/]*/image|bing\\.com/[^/]*/image)', 'i');
    if (imageHosts.test(url)) return true;

    // For any other URL that might be an image, we'll try to load it
    // This is more permissive but safer for user-generated content
    return true;
};

// Helper function to format date
const formatDate = (dateString) => {
    if (!dateString) return 'Recent';

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} week${Math.ceil(diffDays / 7) > 1 ? 's' : ''} ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} month${Math.ceil(diffDays / 30) > 1 ? 's' : ''} ago`;
    return `${Math.ceil(diffDays / 365)} year${Math.ceil(diffDays / 365) > 1 ? 's' : ''} ago`;
};

// Helper function to generate initials from name
const generateInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase().slice(0, 2);
};

// Helper function to get the correct verification icon color
// Prioritizes accent color over verifiedBadgeColor unless verifiedBadgeColor is explicitly customized
export const getVerificationIconColor = (styleConfig) => {
    const accentColor = styleConfig?.accentColor;
    const verifiedBadgeColor = styleConfig?.review?.verifiedBadgeColor;
    const defaultVerifiedColor = '#4A90E2';

    // If verifiedBadgeColor is set to something other than default, use it
    // Otherwise, use accent color
    return (verifiedBadgeColor && verifiedBadgeColor !== defaultVerifiedColor)
        ? verifiedBadgeColor
        : accentColor || defaultVerifiedColor;
};

// Helper function to get the correct read more color
// Prioritizes accent color over readMoreColor unless readMoreColor is explicitly customized
export const getReadMoreColor = (styleConfig) => {
    const accentColor = styleConfig?.accentColor;
    const readMoreColor = styleConfig?.review?.readMoreColor;
    const defaultReadMoreColor = '#4A90E2';

    // If readMoreColor is set to something other than default, use it
    // Otherwise, use accent color
    return (readMoreColor && readMoreColor !== defaultReadMoreColor)
        ? readMoreColor
        : accentColor || defaultReadMoreColor;
};

// Review card component that works with normalized data
export const ReviewCard = ({ review, showPlatformIcon = true, className = "", accentColor = null }) => {
    const normalizedReview = normalizeReviewData(review);

    return (
        <div className={`bg-white p-6 rounded-lg shadow-sm border ${className}`}>
            <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {isValidImageUrl(normalizedReview.avatar) ? (
                        <img
                            src={normalizedReview.avatar}
                            alt={normalizedReview.name}
                            className="w-10 h-10 rounded-full object-cover"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextElementSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div className={`w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium ${isValidImageUrl(normalizedReview.avatar) ? 'hidden' : ''}`}>
                        {generateInitials(normalizedReview.name)}
                    </div>
                </div>

                {/* Review Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                            <h4 className="font-medium text-gray-900">{normalizedReview.name}</h4>
                            {normalizedReview.isVerified && (
                                <VerificationIcon size="16" accentColor={accentColor} />
                            )}
                        </div>
                        {showPlatformIcon && (
                            <PlatformIcon platform={normalizedReview.platform} className="w-5 h-5" />
                        )}
                    </div>

                    <div className="flex items-center space-x-2 mb-2">
                        <StarRating rating={normalizedReview.rating} size="w-4 h-4" color={accentColor} />
                        <span className="text-sm text-gray-500">{normalizedReview.date}</span>
                    </div>

                    {normalizedReview.comment && (
                        <p className="text-gray-700 text-sm leading-relaxed">
                            {normalizedReview.comment}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

// Loading state component for reviews
export const ReviewsLoadingState = ({ count = 3 }) => {
    return (
        <div className="space-y-4">
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/3"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/4"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-full"></div>
                            <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};

// Empty state component for when no reviews are found
export const ReviewsEmptyState = ({ message = "No reviews found", showIcon = true }) => {
    return (
        <div className="text-center py-12">
            {showIcon && (
                <div className="text-gray-400 mb-4">
                    <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                </div>
            )}
            <p className="text-gray-500 text-sm">{message}</p>
        </div>
    );
}; 